import graphqlClient from "../utils/graphql_client_util";
import { SessionStorageUtil } from "../utils/session_storage_util";
import {
  decryptAsyncData,
  serialNumberVerifySubscription,
} from "./pocket_peace";

const MUTATIONS = {
  PAYMENT_REQUEST: `
    mutation PaymentRequest($input: PaymentInput!) {
      paymentRequest(
        input: $input
      ) {
        success
        error {
          message
          errorType
          code
        }
        data {
          paymentFormUrl
          orderNumber
          formParams
        }
      }
    }
  `,
  EZ_EDU_PAYMENT_REQUEST: `
    mutation EzEduPaymentRequest($input: EzEduPaymentRequestInput!) {
      ezEduPaymentRequest(
        input: $input
      ) {
        success
        error {
          message
          errorType
          code
        }
        data {
          paymentFormUrl
          orderNumber
          formParams
        }
      }
    }
  `,
  RESEND_COUPON: `
    mutation ResendCoupon($couponSeq: Int!, $phoneNumber: String!) {
      resendCoupon(couponSeq: $couponSeq, phoneNumber: $phoneNumber) {
        success
        error {
          message
          errorType
          code
        }
        data {
          success
          message
        }
      }
    }
  `,
  CREATE_SUBSCRIPTION: `
    mutation CreateSubscription($input: SubscriptionRequestInput!) {
      createSubscription(
        input: $input
      ) {
        success
        error {
          code
          errorType
          message
        }
        data {
          userPhone
          suspendReason
          suspendDate
          subscriptionSeq
          statusName
          status
          regDate
          productType
          productName
          productId
          paymentDay
          nextPaymentDate
          modDate
          isOverdue
          firstPayment {
            amount
            approvalNumber
            orderNumber
            paymentDate
            transactionSeq
          }
          daysUntilPayment
          cardInfo
          cancelReason
          cancelDate
          amount
        }
      }
    }
  `,
  CANCEL_SUBSCRIPTION: `
    mutation CancelSubscription($subscriptionSeq: Int!, $cancelReason: String) {
      cancelSubscription(subscriptionSeq: $subscriptionSeq, cancelReason: $cancelReason) {
        success
        error {
          code
          errorType
          message
        }
        data {
          subscriptionSeq
          productName
          dbUpdated
          cardDeregistered
          canceledAt
          cancelReason
          amount
        }
      }
    }
  `,
  CANCEL_PAYMENT: `
    mutation RequestPaymentCancel($input: CancelRequestInput!) {
      requestPaymentCancel(input: $input) {
        success
        error {
          code
          errorType
          message
        }
        data
      }
    }
  `,
};

const QUERIES = {
  PAYMENT_TRANSACTION_LIST: `
    query PaymentTransactionList($input: PaymentTransactionSearchInput!) {
      paymentTransactionList(input: $input) {
        totalPages
        totalElements
        success
        size
        page
        error {
          code
          errorType
          message
        }
        data {
          userName
          userId
          usedAt
          serialNumber
          regDate
          phoneNumber
          paymentTransactionsSeq
          paymentMethod
          name
          isUsed
          couponsSeq
          couponProductsSeq
          buyerName
          billingSubscriptionsSeq
          isDonationSubscriptionActive
          billingStatus
          amount
          status
          orderId
        }
      }
    }
  `,
  EZ_EDU_PAYMENT_BY_ORDER_ID: `
    query EzEduPaymentByOrderId($orderId: String!) {
      ezEduPaymentByOrderId(orderId: $orderId) {
        success
        error {
          code
          errorType
          message
        }
        data {
          accountRequestSmsDate
          adminMemo
          adminUserId
          amount
          approvalNumber
          buyerName
          cancelReason
          cancelRequestDate
          cardInfo
          ezEduPaymentTransactionsSeq
          modDate
          orderId
          paymentContent
          paymentMethod
          pgCancelResult
          phoneNumber
          receiptRequested
          refundAccountInfo
          refundAmount
          refundProcessedDate
          refundType
          regDate
          resultCode
          resultMessage
          status
          transactionId
          userRef
        }
      }
    }
  `,
};

export const paymentRequest = async (
  phoneNumber,
  paymentMethod = "card", // card or transfer(계좌이체)
  couponProductsSeq = 0,
  salesManCode = "",
  buyerName = "",
  paymentContent = "",
  amount = "",
  receiptRequested = false,
  userRef = ""
) => {
  try {
    let params = {
      paymentMethod,
      couponProductsSeq: parseInt(couponProductsSeq),
      buyerName,
      phoneNumber,
    };

    if (amount) {
      params["amount"] = parseInt(amount);
    }
    if (paymentContent) {
      params["paymentContent"] = paymentContent;
    }
    if (salesManCode) {
      params["salesManCode"] = salesManCode;
    }
    if (userRef) {
      params["userRef"] = userRef;
    }

    const data = await graphqlClient.mutation(
      MUTATIONS.PAYMENT_REQUEST,
      {
        input: params,
      },
      { useAuth: true }
    );

    if (data.paymentRequest.success) {
      return {
        success: true,
        data: data.paymentRequest.data,
      };
    } else {
      return {
        success: false,
        error: data.paymentRequest.error,
      };
    }
  } catch (err) {
    console.error(err);

    return {};
  }
};

export const paymentHistory = async (page = 0, pageSize = 10) => {
  try {
    let params = {
      size: pageSize,
      phoneNumber: SessionStorageUtil.getItem("phoneNumber"),
      page: page - 1,
      isDonate: "N",
      status: "completed",
    };

    const data = await graphqlClient.query(
      QUERIES.PAYMENT_TRANSACTION_LIST,
      {
        input: params,
      },
      { useAuth: true }
    );

    if (data.paymentTransactionList.success) {
      return data.paymentTransactionList;
    } else {
      return {
        success: false,
        error: data.paymentTransactionList.error,
      };
    }
  } catch (err) {
    console.error(err);

    return {};
  }
};

export const couponMessageResend = async (coupon_id, phone_number) => {
  try {
    const data = await graphqlClient.mutation(
      MUTATIONS.RESEND_COUPON,
      {
        couponSeq: coupon_id,
        phoneNumber: phone_number,
      },
      { useAuth: true }
    );

    if (data.resendCoupon.success) {
      return {
        success: true,
        data: data.resendCoupon.data,
      };
    } else {
      return {
        success: false,
        error: data.resendCoupon.error,
      };
    }
  } catch (err) {
    console.error(err);

    return {};
  }
};

export const paymentCancel = async (
  order_id,
  bankName,
  accountHolder,
  accountNumber,
  cancel_reason = "",
  userRef = ""
) => {
  try {
    const variables = {
      input: {
        orderId: order_id,
        bankName,
        accountHolder,
        accountNumber,
      },
    };

    if (cancel_reason) {
      variables["input"]["cancelReason"] = cancel_reason;
    }

    if (userRef) {
      variables["input"]["userRef"] = userRef;
    }

    const data = await graphqlClient.mutation(
      MUTATIONS.CANCEL_PAYMENT,
      variables,
      { useAuth: true }
    );

    if (data.requestPaymentCancel.success) {
      return {
        success: true,
        data: data.requestPaymentCancel.data,
      };
    } else {
      return {
        success: false,
        error: data.requestPaymentCancel.error,
      };
    }
  } catch (err) {
    console.error(err);

    return {};
  }
};

export const subscriptionRegister = async (
  product_id = "",
  amount = "",
  payment_day = "",
  cardNumb = "",
  expiryDate = "",
  password2 = "",
  userInfo = "",
  buyer_name = "",
  payment_content = "",
  agent_code = "",
  receipt_requested = false,
  userRef = ""
) => {
  try {
    let params = {
      productId: product_id,
      amount: amount,
      paymentDay: payment_day,
      cardInfo: {
        cardNumber: cardNumb,
        expiryDate: expiryDate,
        password2: password2,
        userInfo: userInfo,
      },
      buyerName: buyer_name,
      receiptYn: receipt_requested ? "Y" : "N",
    };

    if (agent_code) {
      params["agentCode"] = agent_code;
    }
    if (payment_content) {
      params["paymentContent"] = payment_content;
    }
    if (userRef) {
      params["userRef"] = userRef;
    }

    const data = await graphqlClient.mutation(
      MUTATIONS.CREATE_SUBSCRIPTION,
      { input: params },
      { useAuth: true }
    );

    if (data.createSubscription.success) {
      return {
        success: true,
        data: data.createSubscription.data,
      };
    } else {
      return {
        success: false,
        error: data.createSubscription.error,
      };
    }
  } catch (err) {
    console.error(err);

    return {};
  }
};

export const donationList = async (page = 1, pageSize = 10) => {
  try {
    let params = {
      size: pageSize,
      phoneNumber: SessionStorageUtil.getItem("phoneNumber"),
      page: page - 1,
      isDonate: "Y",
      status: "done",
    };

    const data = await graphqlClient.query(
      QUERIES.PAYMENT_TRANSACTION_LIST,
      {
        input: params,
      },
      { useAuth: true }
    );

    if (data.paymentTransactionList.success) {
      return data.paymentTransactionList;
    } else {
      return {
        success: false,
        error: data.paymentTransactionList.error,
      };
    }
  } catch (err) {
    console.error(err);

    return {};
  }
};

export const subscriptionCancel = async (
  subscription_id,
  cancel_reason = ""
) => {
  try {
    const data = await graphqlClient.mutation(
      MUTATIONS.CANCEL_SUBSCRIPTION,
      { subscriptionSeq: subscription_id, cancelReason: cancel_reason },
      { useAuth: true }
    );

    if (data.cancelSubscription.success) {
      return {
        success: true,
        data: data.cancelSubscription.data,
      };
    } else {
      return {
        success: false,
        error: data.cancelSubscription.error,
      };
    }
  } catch (err) {
    console.error(err);

    return {};
  }
};

/**
 * EzEdu 결제 요청
 */
export const ezEduPaymentRequest = async (
  productsSeq,
  eduContentsType,
  applicantName,
  zipCode,
  address,
  addressDetail = "",
  grade = "",
  phoneNumber,
  hopeYearMonth,
  amount,
  paymentMethod = "card",
  paymentContent = "",
  receiptYn = "N",
  userRef = ""
) => {
  try {
    let params = {
      productsSeq,
      eduContentsType,
      applicantName,
      zipCode,
      address,
      phoneNumber,
      hopeYearMonth,
      amount: parseInt(amount),
      paymentMethod,
      receiptYn,
    };

    if (addressDetail) {
      params["addressDetail"] = addressDetail;
    }
    if (grade) {
      params["grade"] = grade;
    }
    if (paymentContent) {
      params["paymentContent"] = paymentContent;
    }
    if (userRef) {
      params["userRef"] = userRef;
    }

    const decrypt = await decryptAsyncData(
      SessionStorageUtil.getItem("encSerial")
    );

    if (!decrypt["data"]) {
      return false;
    }

    const serialNumber = decrypt["data"]["encData"];

    const verify = await serialNumberVerifySubscription(serialNumber);

    if (verify["success"] === false) {
      alert(verify["error"]["message"]);
      return {};
    }

    params["serialNumber"] = serialNumber;

    const data = await graphqlClient.mutation(
      MUTATIONS.EZ_EDU_PAYMENT_REQUEST,
      {
        input: params,
      },
      { useAuth: true }
    );

    if (data.ezEduPaymentRequest.success) {
      return {
        success: true,
        data: data.ezEduPaymentRequest.data,
      };
    } else {
      return {
        success: false,
        error: data.ezEduPaymentRequest.error,
      };
    }
  } catch (err) {
    console.error(err);

    return {};
  }
};

/**
 * EzEdu 결제 조회
 */
export const ezEduPaymentByOrderId = async (orderId) => {
  try {
    const data = await graphqlClient.query(
      QUERIES.EZ_EDU_PAYMENT_BY_ORDER_ID,
      {
        orderId: orderId,
      },
      { useAuth: true }
    );

    if (data.ezEduPaymentByOrderId.success) {
      return {
        success: true,
        data: data.ezEduPaymentByOrderId.data,
      };
    } else {
      return {
        success: false,
        error: data.ezEduPaymentByOrderId.error,
      };
    }
  } catch (err) {
    console.error(err);

    return {};
  }
};
